import { ContactFormSection } from "@/types/dto/destinations-page/destination-page-types";
import { Box, Typography, Grid, Stack } from "@mui/material";
import { CardTravel, PhoneOutlined } from "@mui/icons-material";
import React from "react";
import {ColletteButton, ColletteDivider} from "@collette-travel/collette-common-controls";
import NeedMoreInfoSection from "@/components/forms/need-more-info/need-more-info-section";
import {useTranslations} from "next-intl";
import { SectionHeader } from "../../common/section-header";

interface NeedMoreInformationProps {
  contactFormSection: ContactFormSection;
  moduleRankingPosition?: string;
  tourId?: string;
  tourTitle?: string;
}

const NeedMoreInformationSection: React.FC<NeedMoreInformationProps> = ({
  contactFormSection,
  tourId,
  tourTitle,
  moduleRankingPosition = "17"
}) => {
  const t = useTranslations("FORMS");
  return(
    <Box
      className="fluid-box anchor-section"
      sx={{
        backgroundColor: "background.warm_strong",
        display: "flex",
        alignItems: "center",
        gap: "40px",
        alignSelf: "stretch"
      }}
      data-track="visibility"
      data-parent-container-type="form"
      data-module-name="Contact Form Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      component="section"
    >
      <Stack
        className="destination-page-container"
        sx={{
          padding: {
            zero: "4rem 2.4rem",
            legacyTablet: "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
          },
          gap: "2.4rem"
        }}
      >
        <SectionHeader
          headerText={contactFormSection.contactFormIntro.headerText}
          copyText={contactFormSection.contactFormIntro.copyText}
          headerTestId="contact-form-header"
          copyTestId="contact-form-copy"
          headerSx={{
            textTransform: "none !important",
            fontSize: {xs: "2.6rem", sm: "2.8rem", lg: "3.2rem"},
            textWrap: "wrap"
          }}
        />
        <Grid
          container
          sx={{
            gap: { xs: "4rem", lg: "6rem" },
            alignItems: "flex-start"
          }}
        >
          <Grid size={{xs: 12, md: 12, lg: 7}}>
              <Box
                id="contact-us-form"
                height="100%"
                sx={{
                  display: "flex",
                  borderRadius: "1.25rem",
                  backgroundColor: "background.default",
                  padding: { xs: "2.4rem 2.4rem 0.4rem", md: "4rem 4rem 2rem" },
                  boxShadow: 6,
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: { lg: "66.8rem" }
                }}
              >
                <NeedMoreInfoSection
                  tourId={tourId ?? ""}
                  tourTitle={tourTitle ?? ""}
                />
              </Box>
          </Grid>
          <Grid size={{xs: 12, md: 12, lg: "grow"}}>
            <Stack
              sx={{
                gap: "4rem",
                paddingX: { xs: "2.4rem", md: "4rem", lg: 0 },
                marginTop: { lg: "14rem" }
            }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.6rem"
                }}
              >
                <Box>
                  <PhoneOutlined fontSize="large" sx={{ marginBottom: "1.6rem" }}/>
                  <Typography
                    variant="h6"
                    component="p"
                    sx={{
                      fontSize: "1.8rem",
                      fontWeight: "600 !important",
                      display: "flex",
                      alignItems: "start",
                      gap: "0.8rem",
                      flexDirection: {xs: "column", sm: "row"},
                      marginBottom: "1.2rem"
                    }}
                    data-testid="contact-form-call-to-action"
                  >
                    {contactFormSection.contactFormIntro.callToActionText}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    component="p"
                  >
                    {t("Call us toll free at")}
                    {" "}
                    <a
                      href={`tel:${contactFormSection.phoneNumber}`}
                      style={{
                        fontWeight: "600",
                        fontSize: "1.8rem",
                        color: "var(--theme-palette-secondary-main)",
                        textDecoration: "underline"
                      }}
                      data-testid="contact-form-phone-number"
                      data-track="click"
                    >
                      {contactFormSection.phoneNumber}
                    </a>
                  </Typography>
                </Box>
              </Box>
              <ColletteDivider/>
              <Box flexDirection="column">
                <CardTravel fontSize="large" sx={{marginBottom: "1.6rem"}} />
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{marginBottom: "1.2rem"}}
                  data-testid="contact-form-login-cta-header"
                >
                  {contactFormSection.contactFormLoginCTA.headerText}
                </Typography>
                <Typography
                  variant="subtitle1"
                  component="p"
                  sx={{marginBottom: "2.4rem"}}
                  data-testid="contact-form-login-cta-copy"
                >
                  {contactFormSection.contactFormLoginCTA.copyText}
                </Typography>
                <ColletteButton
                  variant="outlined"
                  size="large"
                  label="Log In"
                  testId="contactform-login-button"
                  data-track="click"
                  onClick={() => document.getElementById("nav-login-button")?.click()}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
export default NeedMoreInformationSection;
